const TOKEN_KEY = 'map_token';
const USER_KEY = 'map_user';
const ORIGINAL_TOKEN_KEY = 'map_original_token';

export interface AuthUser {
  sub: string;
  /** @deprecated Super-admin/impersonation only. Use roles[] for tenant users. */
  role?: string;
  tenantId?: string;
  tenantName?: string;
  tenantSlug?: string;
  email?: string;
  name?: string;
  impersonating?: boolean;
  /** RBAC roles assigned to this user (tenant users only) */
  roles?: string[];
  /** Flat list of resolved permissions (tenant users only) */
  permissions?: string[];
  /** Tag-based scope constraints per resource (tenant users only) */
  scopeConstraints?: Record<string, { tags?: string[] }>;
  exp?: number; // JWT expiry (Unix seconds)
  iat?: number; // JWT issued-at (Unix seconds)
}

/**
 * Checks if the current user has a given permission.
 * Super-admin impersonation tokens bypass all checks (always returns true).
 * Returns false if no user is authenticated.
 */
export function hasPermission(user: AuthUser | null, permission: string): boolean {
  if (!user) return false;
  if (user.impersonating === true) return true;
  if (user.role === 'super_admin') return true;
  return user.permissions?.includes(permission) ?? false;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
  try {
    const payload = parseJwt(token);
    if (payload) localStorage.setItem(USER_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(ORIGINAL_TOKEN_KEY);
}

export function getCurrentUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) {
    // Try to parse from token if available
    const token = getToken();
    if (token) {
      const payload = parseJwt(token);
      return payload as AuthUser | null;
    }
    return null;
  }
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

// ─── Impersonation ────────────────────────────────────────────────────────────

export function isImpersonating(): boolean {
  return !!localStorage.getItem(ORIGINAL_TOKEN_KEY);
}

/**
 * Saves the current super-admin token and activates the impersonation token.
 * The original token is preserved so it can be restored later.
 *
 * IMPORTANT: on a tenant subdomain there may be no prior token (cross-domain
 * redirect scenario). In that case we still set ORIGINAL_TOKEN_KEY to a
 * sentinel value so that isImpersonating() returns true and getAuthMode()
 * correctly resolves to 'tenant'.
 */
export function startImpersonation(impersonationToken: string): void {
  const current = getToken();
  // Always mark impersonation state — use the current token if available,
  // or a sentinel so isImpersonating() is reliably true.
  localStorage.setItem(ORIGINAL_TOKEN_KEY, current ?? '__cross_domain__');
  setToken(impersonationToken);
}

/**
 * Ends impersonation on a tenant subdomain.
 * The original super-admin token lives on admin.<base-domain>, so we simply
 * redirect there. The super-admin session is already intact on that subdomain.
 */
export function stopImpersonation(): void {
  // Restore the original super-admin token (if it was saved on same domain)
  const original = localStorage.getItem(ORIGINAL_TOKEN_KEY);
  localStorage.removeItem(ORIGINAL_TOKEN_KEY);
  const base = (import.meta.env.VITE_TENANT_BASE_DOMAIN as string | undefined)?.trim() ?? '';
  const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (base && !isLocal) {
    window.location.href = `https://admin.${base}`;
  } else {
    // Local dev: restore original token and stay on same origin
    if (original && original !== '__cross_domain__') {
      setToken(original);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    }
    window.location.href = '/admin';
  }
}

// ─── Mode ─────────────────────────────────────────────────────────────────────

/**
 * Returns the current UI mode:
 * - "admin"  → super_admin token without active impersonation
 * - "tenant" → any tenant-scoped token (regular user OR super_admin impersonating)
 */
export function getAuthMode(): 'admin' | 'tenant' {
  const user = getCurrentUser();
  if (user?.role === 'super_admin' && !isImpersonating()) return 'admin';
  return 'tenant';
}

// ─── Internal ─────────────────────────────────────────────────────────────────

function parseJwt(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split('.')[1];
    if (!base64) return null;
    const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}
